export type FunnelEventName =
  | "landing_view"
  | "cta_click"
  | "login_view"
  | "video_load"
  | "video_start"
  | "video_complete"
  | "request_form_start"
  | "validation_error"
  | "request_submitted"
  | "approval"
  | "first_successful_sign_in";

export type FunnelPage = "landing" | "login" | "verify";
export type FunnelVariant = "control" | "variant";

const ANONYMOUS_ID_KEY = "spn_funnel_anonymous_id";
const SOURCE_KEY = "spn_funnel_source";
const CAMPAIGN_KEY = "spn_funnel_campaign";
const VARIANT_KEY = "spn_funnel_variant";
const SAFE_DIMENSION = /^[a-z0-9][a-z0-9._-]*$/;

function safeDimension(value: string | null, max: number): string | null {
  const clean = value?.trim().toLowerCase().slice(0, max) ?? "";
  return clean && SAFE_DIMENSION.test(clean) ? clean : null;
}

function storageGet(key: string): string | null {
  try {
    return window.localStorage.getItem(key);
  } catch {
    return null;
  }
}

function storageSet(key: string, value: string): void {
  try {
    window.localStorage.setItem(key, value);
  } catch {
    // Analytics should never block the product when storage is unavailable.
  }
}

function makeAnonymousId(): string {
  const random =
    typeof crypto !== "undefined" && "randomUUID" in crypto
      ? crypto.randomUUID()
      : `${Date.now()}-${Math.random().toString(36).slice(2)}`;
  return `spn_${random.replace(/[^a-zA-Z0-9_-]/g, "").toLowerCase()}`;
}

function deterministicVariant(id: string): FunnelVariant {
  let hash = 0;
  for (const char of id) hash = (hash * 31 + char.charCodeAt(0)) | 0;
  return Math.abs(hash) % 2 === 0 ? "control" : "variant";
}

export function getFunnelContext(): {
  anonymousId: string;
  source: string | null;
  campaign: string | null;
  variant: FunnelVariant;
} {
  if (typeof window === "undefined") {
    return { anonymousId: "spn_server_context", source: null, campaign: null, variant: "control" };
  }
  const anonymousId = storageGet(ANONYMOUS_ID_KEY) ?? makeAnonymousId();
  storageSet(ANONYMOUS_ID_KEY, anonymousId);

  const params = new URLSearchParams(window.location.search);
  const source =
    safeDimension(params.get("utm_source") ?? params.get("source"), 40) ??
    safeDimension(storageGet(SOURCE_KEY), 40);
  const campaign =
    safeDimension(params.get("utm_campaign") ?? params.get("campaign"), 80) ??
    safeDimension(storageGet(CAMPAIGN_KEY), 80);
  const queryVariant = params.get("variant");
  const variant: FunnelVariant =
    queryVariant === "control" || queryVariant === "variant"
      ? queryVariant
      : (storageGet(VARIANT_KEY) as FunnelVariant | null) ?? deterministicVariant(anonymousId);
  if (source) storageSet(SOURCE_KEY, source);
  if (campaign) storageSet(CAMPAIGN_KEY, campaign);
  storageSet(VARIANT_KEY, variant);
  return { anonymousId, source, campaign, variant };
}

export function getLoginHref(tab: "signin" | "request"): string {
  const context = getFunnelContext();
  const params = new URLSearchParams({ tab });
  if (context.source) params.set("utm_source", context.source);
  if (context.campaign) params.set("utm_campaign", context.campaign);
  params.set("variant", context.variant);
  return `/login?${params.toString()}`;
}

export function trackFunnelEvent(eventName: FunnelEventName, page?: FunnelPage): void {
  if (typeof window === "undefined") return;
  const context = getFunnelContext();
  void fetch("/api/analytics/events", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    credentials: "include",
    keepalive: true,
    body: JSON.stringify({
      eventName,
      anonymousId: context.anonymousId,
      source: context.source,
      campaign: context.campaign,
      variant: context.variant,
      page,
    }),
  }).catch(() => {
    // Measurement is deliberately best-effort and must not affect conversion.
  });
}